import { normalizeCountryCode } from "@src/packages/location/countryPreference";
import type { MedicationBody, MedicationResource } from "./types";
import { getUsMedicationResource } from "./modules/medications/us-resource-audit";

export type MedicationMarketContext = {
  /** Country where treatment/care is taking place, when known. */
  careCountry?: string | null;
  /** Patient residence/profile country, used when care country is unknown. */
  homeCountry?: string | null;
  /** Device or product fallback for anonymous/incomplete profiles. */
  fallbackCountry?: string | null;
};

/**
 * Resolve market independently from language. Care country wins over home
 * country, followed by the explicit fallback and finally the U.S. launch
 * default. Locale is intentionally not an input to this function.
 */
export function resolveMedicationMarketCountry(
  context: MedicationMarketContext = {},
): string {
  return (
    normalizeCountryCode(context.careCountry) ??
    normalizeCountryCode(context.homeCountry) ??
    normalizeCountryCode(context.fallbackCountry) ??
    "US"
  );
}

type FrenchResourceLabel = (brand: string) => string;

function getFrenchOfficialMedicationResources(
  body: MedicationBody,
  labelForBrand: FrenchResourceLabel,
): MedicationResource[] {
  const seenUrls = new Set<string>();
  const resources: MedicationResource[] = [];

  for (const entry of body.brandsByCountry ?? []) {
    if (entry.country !== "FR" || !entry.cisByBrand) continue;

    for (const brand of entry.brands) {
      const cis = entry.cisByBrand[brand];
      if (!cis) continue;

      const url = `https://base-donnees-publique.medicaments.gouv.fr/medicament/${cis}/extrait`;
      if (seenUrls.has(url)) continue;
      seenUrls.add(url);

      resources.push({
        label: labelForBrand(brand),
        url,
        type: "info",
        market: "FR",
        authority: "ANSM/BDPM",
        audience: "patient-education",
        medicationMatch: brand,
        verifiedOn: "2026-07-17",
      });
    }
  }

  return resources;
}

function dedupeMedicationResources(
  resources: MedicationResource[],
): MedicationResource[] {
  const seenUrls = new Set<string>();
  return resources.filter((resource) => {
    if (seenUrls.has(resource.url)) return false;
    seenUrls.add(resource.url);
    return true;
  });
}

/**
 * Select resources for the care market, retaining only genuinely global
 * links plus resources whose explicit market matches. U.S. links come from
 * the audited catalog; French ANSM/BDPM links are derived from French CIS
 * metadata. The locale used to render labels never changes this selection.
 */
export function resolveMedicationResources(
  body: MedicationBody,
  medicationId: string,
  marketContext: MedicationMarketContext = {},
  labelForFrenchBrand: FrenchResourceLabel = (brand) =>
    `${brand}: ANSM/BDPM patient notice`,
): MedicationResource[] {
  const market = resolveMedicationMarketCountry(marketContext);
  const authoredResources = (body.resources ?? []).filter(
    (resource) =>
      !resource.market ||
      resource.market === "GLOBAL" ||
      resource.market === market,
  );
  const marketResources: MedicationResource[] = [];

  if (market === "US") {
    const resource = getUsMedicationResource(medicationId);
    if (resource) marketResources.push(resource);
  }

  if (market === "FR") {
    marketResources.push(
      ...getFrenchOfficialMedicationResources(body, labelForFrenchBrand),
    );
  }

  return dedupeMedicationResources([...authoredResources, ...marketResources]);
}
